import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2, MessageCircle, RefreshCw, X } from 'lucide-react';
import { API } from '../App';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

let sdkPromise = null;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const loadFacebookSdk = ({ appId, graphVersion }) => {
  if (window.FB) {
    window.FB.init({ appId, cookie: true, xfbml: false, version: graphVersion });
    return Promise.resolve(window.FB);
  }
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const existingInit = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof existingInit === 'function') existingInit();
      window.FB.init({ appId, cookie: true, xfbml: false, version: graphVersion });
      resolve(window.FB);
    };
    const existingScript = document.getElementById('facebook-jssdk');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.onerror = () => {
        sdkPromise = null;
        reject(new Error('Failed to load the Meta signup SDK.'));
      };
      document.body.appendChild(script);
    }
  });
  return sdkPromise;
};

const isTrustedMetaOrigin = (origin) => {
  try {
    const url = new URL(origin);
    return url.protocol === 'https:' && (url.hostname === 'facebook.com' || url.hostname.endsWith('.facebook.com'));
  } catch {
    return false;
  }
};

const parseSignupMessage = (event) => {
  if (!isTrustedMetaOrigin(event.origin)) return null;
  let payload = event.data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return null;
  if (!['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(payload.event)) return null;
  const data = payload.data && typeof payload.data === 'object' ? payload.data : {};
  const wabaId = data.waba_id || data.wabaId;
  const phoneNumberId = data.phone_number_id || data.phoneNumberId || null;
  return wabaId ? { wabaId: String(wabaId), phoneNumberId: phoneNumberId ? String(phoneNumberId) : null } : null;
};

const WhatsAppConnectionManager = ({ onClose }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const signupCleanupRef = useRef(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/whatsapp/coexistence/config`, { headers: authHeaders() });
      setConfig(response.data);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not load WhatsApp connection settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => () => {
    signupCleanupRef.current?.();
  }, []);

  const connect = async () => {
    if (connecting) return;
    setError('');
    setSuccess('');

    if (!config?.enabled) {
      setError('Embedded Signup is not configured. Set WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID on the backend.');
      return;
    }
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    if (window.location.protocol !== 'https:' && !isLocalhost) {
      setError('Meta Embedded Signup requires HTTPS.');
      return;
    }

    setConnecting(true);
    let authorizationCode = null;
    let signupResult = null;
    let completed = false;

    let timeoutId = null;
    const cleanup = () => {
      window.removeEventListener('message', onSignupMessage);
      if (timeoutId) window.clearTimeout(timeoutId);
      if (signupCleanupRef.current === cleanup) signupCleanupRef.current = null;
    };
    const finishIfReady = async () => {
      if (completed || !authorizationCode || !signupResult) return;
      completed = true;
      cleanup();
      try {
        const response = await axios.post(
          `${API}/whatsapp/coexistence/complete`,
          {
            code: authorizationCode,
            waba_id: signupResult.wabaId,
            phone_number_id: signupResult.phoneNumberId
          },
          { headers: authHeaders() }
        );
        setConfig((current) => ({ ...current, connection: response.data.connection }));
        setSuccess('WhatsApp Business App coexistence is connected and webhook delivery is subscribed.');
      } catch (requestError) {
        setError(requestError.response?.data?.detail || 'Could not complete WhatsApp coexistence setup.');
      } finally {
        setConnecting(false);
      }
    };
    function onSignupMessage(event) {
      const parsed = parseSignupMessage(event);
      if (!parsed) return;
      signupResult = parsed;
      finishIfReady();
    }
    signupCleanupRef.current?.();
    signupCleanupRef.current = cleanup;
    timeoutId = window.setTimeout(() => {
      if (!completed) {
        cleanup();
        setConnecting(false);
        setError('Meta signup timed out before all connection details were returned. Please try again.');
      }
    }, 120000);
    window.addEventListener('message', onSignupMessage);

    try {
      const fb = await loadFacebookSdk({ appId: config.app_id, graphVersion: config.graph_version });
      fb.login((response) => {
        const code = response?.authResponse?.code;
        if (!code) {
          cleanup();
          setConnecting(false);
          setError('WhatsApp signup was cancelled or did not return an authorization code.');
          return;
        }
        authorizationCode = code;
        finishIfReady();
      }, {
        config_id: config.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3'
        }
      });
    } catch (sdkError) {
      cleanup();
      setConnecting(false);
      setError(sdkError.message || 'Could not start Meta Embedded Signup.');
    }
  };

  const connection = config?.connection;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="whatsapp-connection-manager">
      <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border-[var(--tg-border-soft)] bg-[var(--tg-surface)] text-[var(--tg-text-primary)]">
        <CardHeader className="border-b border-[var(--tg-border-soft)]">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>Manage WhatsApp</CardTitle>
                <p className="mt-1 text-sm text-[var(--tg-text-muted)]">Connect the WhatsApp Business App through Meta coexistence.</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close WhatsApp manager">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 p-6">
          {error && <Alert className="border-red-500/50 bg-red-500/10 text-red-300"><AlertDescription>{error}</AlertDescription></Alert>}
          {success && <Alert className="border-emerald-500/50 bg-emerald-500/10 text-emerald-300"><AlertDescription>{success}</AlertDescription></Alert>}

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin" /></div>
          ) : connection ? (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                <div className="min-w-0">
                  <p className="font-semibold">{connection.verified_name || 'WhatsApp Business number'}</p>
                  <p className="mt-1 text-sm text-[var(--tg-text-muted)]">{connection.display_phone_number || 'Phone number verified by Meta'}</p>
                  <p className="mt-2 break-all text-xs text-[var(--tg-text-muted)]">WABA {connection.waba_id} · Phone ID {connection.phone_number_id}</p>
                  <span className="mt-3 inline-flex rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-300">Active coexistence</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] p-5 text-sm text-[var(--tg-text-muted)]">
              No WhatsApp coexistence number is connected yet. Meta will ask a Business Portfolio admin to select and approve the eligible WhatsApp Business App number.
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={loadConfig} disabled={loading || connecting}>
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button onClick={connect} disabled={loading || connecting || !config?.enabled} className="bg-emerald-600 text-white hover:bg-emerald-700">
              {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-2 h-4 w-4" />}
              {connection ? 'Reconnect WhatsApp Business App' : 'Connect WhatsApp Business App'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppConnectionManager;
